import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ArrowLeft, TrendingUp, Users, Server, Flame, Map as MapIcon, Target, Trophy } from "lucide-react";
import TopNav from "../components/TopNav";
import FavoritesSheet from "../components/FavoritesSheet";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { fetchTrends } from "../lib/api";
import { useGame } from "../context/GameContext";
import { formatNumber } from "../lib/format";

const RANGES = [
  { id: 7, label: "7D" },
  { id: 30, label: "30D" },
  { id: 90, label: "90D" },
];

const StatPill = ({ icon, label, value, highlight }) => (
  <div
    className="tac-card p-4"
    style={{ background: "var(--bg-surface)" }}
  >
    <span className="corner-tr" /><span className="corner-bl" />
    <div className="flex items-center gap-2 hud-caption">
      <span style={{ color: "var(--primary-accent)" }}>{icon}</span>
      {label}
    </div>
    <div
      className="font-head text-3xl font-extrabold tabular-nums mt-1"
      style={{ color: highlight ? "var(--highlight)" : "var(--text-primary)" }}
    >
      {value}
    </div>
  </div>
);

const TrendsPage = () => {
  const { gameId: paramGameId } = useParams();
  const navigate = useNavigate();
  const { game, gameId, setGameId, games } = useGame();
  const [days, setDays] = useState(30);
  const [favOpen, setFavOpen] = useState(false);
  const activeGameId = paramGameId || gameId;

  useEffect(() => {
    if (paramGameId && paramGameId !== gameId) setGameId(paramGameId);
  }, [paramGameId]); // eslint-disable-line

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["trends", activeGameId, days],
    queryFn: () => fetchTrends(activeGameId, days),
    staleTime: 60_000,
    refetchInterval: 180_000,
    retry: 1,
  });

  const timelineData = useMemo(
    () => (data?.peak_timeline || []).map((p) => ({
      ...p,
      label: p.date.slice(5), // MM-DD
    })),
    [data]
  );
  const maxTopMetric = Math.max(
    ...(data?.top_maps || []).map((m) => m.player_minutes),
    ...(data?.top_modes || []).map((m) => m.player_minutes),
    1
  );

  // SEO: dynamic document title + meta description
  useEffect(() => {
    if (data?.game?.name) {
      const title = `${data.game.name} Server Trends · Peak Players Last ${data.days}D`;
      document.title = title;
      const d = `Live ${data.game.name} server trends: ${formatNumber(data.summary.peak_window)} peak concurrent players in the last ${data.days} days, top maps & modes.`;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = d;
    }
    return () => { document.title = "Server-Recon · Discover Every Game Server"; };
  }, [data]);

  return (
    <div className="min-h-screen" data-testid="trends-page">
      <TopNav onOpenFavorites={() => setFavOpen(true)} />
      <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 hud-caption hover:underline"
          style={{ color: "var(--primary-accent)" }} data-testid="trends-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> BACK TO BROWSER
        </Link>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="hud-caption">GAME TRENDS · LIVE TELEMETRY</div>
            <h1
              className="font-head text-3xl md:text-5xl font-extrabold uppercase mt-1"
              style={{ color: "var(--text-primary)", letterSpacing: "0.02em", lineHeight: 1.1 }}
              data-testid="trends-title"
            >
              {data?.game?.name || game?.name || "—"} <span style={{ color: "var(--primary-accent)" }}>· TRENDS</span>
            </h1>
            <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--text-secondary)" }}>
              Rolling snapshot of concurrent player activity across every tracked server. Updated every 2 minutes.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="hud-caption mr-1">WINDOW:</div>
            {RANGES.map((r) => (
              <Button
                key={r.id}
                data-testid={`range-${r.id}`}
                onClick={() => setDays(r.id)}
                variant="outline"
                className="font-mono-hud tracking-widest"
                style={{
                  background: days === r.id ? "var(--primary-accent)" : "var(--bg-elevated)",
                  color: days === r.id ? "#000" : "var(--text-primary)",
                  borderColor: days === r.id ? "var(--primary-accent)" : "var(--border-strong)",
                  borderRadius: 2,
                }}
              >
                {r.label}
              </Button>
            ))}
            <Button
              onClick={() => navigate(`/rankings/${activeGameId}`)}
              className="font-mono-hud tracking-widest font-bold"
              data-testid="go-to-rankings"
              style={{ background: "var(--secondary-accent)", color: "#000", borderRadius: 2 }}
            >
              <Trophy className="w-4 h-4 mr-2" /> TOP 50
            </Button>
          </div>
        </div>

        {/* Summary pills */}
        {isError ? (
          <div className="tac-card p-6 text-center" data-testid="trends-error" style={{ background: "var(--bg-surface)" }}>
            <span className="corner-tr" /><span className="corner-bl" />
            <div className="hud-caption mb-2" style={{ color: "var(--ping-high)" }}>TELEMETRY UNAVAILABLE</div>
            <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
              Trends compute is taking longer than expected. Try again in a moment.
            </p>
            <Button onClick={() => refetch()} data-testid="trends-retry"
              className="font-mono-hud tracking-widest"
              style={{ background: "var(--primary-accent)", color: "#000", borderRadius: 2 }}>
              RETRY
            </Button>
          </div>
        ) : isLoading && !data ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24" style={{ background: "var(--bg-elevated)" }} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="trends-summary">
            <StatPill icon={<Flame className="w-4 h-4" />} label={`PEAK ${data.days}D`} value={formatNumber(data.summary.peak_window)} highlight />
            <StatPill icon={<Users className="w-4 h-4" />} label="PLAYERS NOW" value={formatNumber(data.summary.current_players)} />
            <StatPill icon={<Server className="w-4 h-4" />} label="ONLINE SERVERS" value={formatNumber(data.summary.online_servers)} />
            <StatPill icon={<TrendingUp className="w-4 h-4" />} label="TRACKED" value={formatNumber(data.summary.total_servers)} />
          </div>
        )}

        {/* Concurrent timeline */}
        <section className="tac-card p-4" style={{ background: "var(--bg-surface)" }}>
          <span className="corner-tr" /><span className="corner-bl" />
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="hud-label">CONCURRENT PLAYERS · DAILY PEAK</div>
              <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
                LAST {days} DAYS
              </div>
            </div>
            <TrendingUp className="w-5 h-5" style={{ color: "var(--primary-accent)" }} />
          </div>
          <div className="h-[280px]" data-testid="trends-timeline-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timelineData} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="peakGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary-accent)" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="var(--primary-accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--border-color)" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="label" stroke="var(--text-dim)" tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }} tickLine={false} minTickGap={16} />
                <YAxis stroke="var(--text-dim)" tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }} tickLine={false} width={46} />
                <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid var(--primary-accent)", borderRadius: 2, fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-primary)" }} />
                <Area type="monotone" dataKey="peak" stroke="var(--primary-accent)" strokeWidth={2} fill="url(#peakGrad)" name="peak" />
                <Area type="monotone" dataKey="avg" stroke="var(--secondary-accent)" strokeWidth={1.5} fill="transparent" strokeDasharray="4 2" name="avg" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex gap-4 text-xs" style={{ color: "var(--text-dim)" }}>
            <span><span className="inline-block w-3 h-3 mr-1 align-middle" style={{ background: "var(--primary-accent)" }} /> Daily peak concurrent</span>
            <span><span className="inline-block w-3 h-3 mr-1 align-middle" style={{ background: "var(--secondary-accent)" }} /> Daily average</span>
          </div>
        </section>

        {/* Top maps & modes */}
        <section className="grid lg:grid-cols-2 gap-4">
          <TopList
            title="TOP MAPS"
            icon={<MapIcon className="w-5 h-5" />}
            items={data?.top_maps}
            max={maxTopMetric}
            testid="top-maps"
            color="var(--primary-accent)"
          />
          <TopList
            title="TOP MODES"
            icon={<Target className="w-5 h-5" />}
            items={data?.top_modes}
            max={maxTopMetric}
            testid="top-modes"
            color="var(--secondary-accent)"
          />
        </section>

        {/* SEO footer */}
        <section className="tac-card p-5" style={{ background: "var(--bg-surface)" }}>
          <span className="corner-tr" /><span className="corner-bl" />
          <h2 className="font-head text-xl uppercase font-bold mb-2" style={{ color: "var(--text-primary)" }}>
            About these {data?.game?.name || "game"} trends
          </h2>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            We poll every tracked server once per minute and store concurrent player counts, map and
            mode rotation and server uptime. The daily peak concurrent player number is the single
            busiest minute of that day across the entire {data?.game?.name || "game"} ecosystem. The
            top-maps and top-modes lists are weighted by player-minutes (i.e. how long each slot was
            actually filled). Want historical data on a specific server? Click any{" "}
            <Link to="/" style={{ color: "var(--primary-accent)" }} className="underline">server in the browser</Link>{" "}
            to see its 24-hour and 30-day roll-ups.
          </p>
        </section>

        <FavoritesSheet open={favOpen} onOpenChange={setFavOpen} onOpenServer={(s) => navigate(`/server/${encodeURIComponent(s.id)}`)} />
      </main>
    </div>
  );
};

const TopList = ({ title, icon, items, max, testid, color }) => (
  <div className="tac-card p-4" style={{ background: "var(--bg-surface)" }} data-testid={testid}>
    <span className="corner-tr" /><span className="corner-bl" />
    <div className="flex items-center justify-between mb-3">
      <div>
        <div className="hud-label">{title}</div>
        <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
          BY PLAYER-MINUTES
        </div>
      </div>
      <span style={{ color: "var(--primary-accent)" }}>{icon}</span>
    </div>
    {!items || items.length === 0 ? (
      <div className="py-10 text-center text-sm" style={{ color: "var(--text-dim)" }}>
        No data yet — check back after the next poll cycle.
      </div>
    ) : (
      <ol className="space-y-2">
        {items.map((row, idx) => {
          const pct = max > 0 ? (row.player_minutes / max) * 100 : 0;
          return (
            <li
              key={row.name}
              className="relative border px-3 py-2"
              style={{ borderColor: "var(--border-color)", background: "var(--bg-main)", borderRadius: 2 }}
              data-testid={`${testid}-row-${idx}`}
            >
              <div
                className="absolute left-0 top-0 bottom-0 opacity-20"
                style={{ background: color, width: `${pct}%` }}
                aria-hidden
              />
              <div className="relative flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono-hud text-xs w-6 text-right" style={{ color: "var(--text-dim)" }}>
                    #{idx + 1}
                  </span>
                  <span className="font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                    {row.name}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono-hud tabular-nums whitespace-nowrap">
                  <span style={{ color: "var(--text-secondary)" }}>{row.servers} srv</span>
                  <span style={{ color: color }}>{formatNumber(row.player_minutes)}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    )}
  </div>
);

export default TrendsPage;
